import {
  Box,
  Tooltip as ChakraTooltip,
  Text,
  TooltipProps,
  VStack,
} from '@chakra-ui/react'
import {
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
} from 'react'

import { TooltipConfig } from '../../datatypes/dataTypeOptions'

export type ConditionalTooltipProps = TooltipProps & {
  children: ReactNode
  isDisabled?: boolean
  tooltipOptions?: TooltipConfig
}

const defaultTooltipOptions: TooltipConfig = {
  placement: 'top',
}

export const ConditionalTooltip = ({
  children,
  isDisabled,
  tooltipOptions,
  ...props
}: ConditionalTooltipProps) => {
  if (!tooltipOptions || isDisabled) return children
  const {
    label,
    asValue,
    useFallback: _useFallback,
    ...chakraTooltipOptions
  } = tooltipOptions

  return (
    <Tooltip
      children={
        <Box
          display="inline"
          _hover={{
            textDecorationColor: 'presets.turquoise.50',
          }}
          textDecoration="underline"
          textDecorationStyle="dashed"
          textDecorationColor="presets.neutral.50"
          textDecorationThickness="1px"
          textUnderlineOffset="5px"
          cursor="pointer"
          paddingBottom="3px"
        >
          {children}
        </Box>
      }
      {...defaultTooltipOptions}
      {...chakraTooltipOptions}
      label={
        <VStack gap="0" alignItems="start">
          {label && <Text fontSize="12px">{label}</Text>}
          {asValue && (
            <Text fontSize="12px" fontWeight="700">
              {asValue}
            </Text>
          )}
        </VStack>
      }
      {...props}
    />
  )
}

export const Tooltip = forwardRef<any, TooltipProps>(
  ({ children, ...props }, ref) => {
    if (!isValidElement(children)) return null

    const child = children as ReactElement<Record<string, any>>

    const childProps = {
      onFocus: (e: React.FocusEvent) => {
        child.props.onFocus?.(e)
        e.preventDefault()
      },
      onBlur: (e: React.FocusEvent) => {
        child.props.onBlur?.(e)
        e.preventDefault()
      },
    }

    return (
      <ChakraTooltip
        ref={ref}
        hasArrow
        openDelay={500}
        {...props}
        shouldWrapChildren={false} //dont touch this. this will add a <span /> which will mess with the css
      >
        {cloneElement(children, childProps)}
      </ChakraTooltip>
    )
  },
)
